import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Download, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayPalSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const executePayment = async () => {
      const orderId = searchParams.get('order_id');
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');
      
      if (!orderId || !paymentId || !payerId) {
        setError('Fehlende Zahlungsinformationen');
        setIsLoading(false);
        return;
      }
      
      try {
        // Execute PayPal payment
        const response = await fetch(
          `${API_URL}/api/orders/paypal/execute/${orderId}?payment_id=${paymentId}&payer_id=${payerId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        const result = await response.json();
        
        if (result.success) {
          // Get order details
          const orderResponse = await fetch(`${API_URL}/api/orders/${orderId}`);
          const orderData = await orderResponse.json();
          setOrder(orderData);
          clearCart();
          toast.success('Zahlung erfolgreich!');
        } else {
          setError(result.error || 'Zahlung fehlgeschlagen');
        }
      } catch (err) {
        console.error('Payment execution error:', err);
        setError('Fehler bei der Zahlungsverarbeitung');
      } finally {
        setIsLoading(false);
      }
    };
    
    executePayment();
  }, [searchParams, clearCart]);
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md text-center p-8">
          <Loader2 className="mx-auto animate-spin text-red-600 mb-4" size={48} />
          <h2 className="text-xl font-bold mb-2">Zahlung wird verarbeitet...</h2>
          <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
        </Card>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center p-8">
          <div className="text-red-600 mb-4">
            <svg className="mx-auto w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2 text-red-600">Zahlung fehlgeschlagen</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/warenkorb')} className="bg-red-600 hover:bg-red-700">
            Zurück zum Warenkorb
          </Button>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl">
        <Card>
          <CardHeader className="bg-green-50 border-b text-center">
            <CheckCircle className="mx-auto text-green-600 mb-4" size={64} />
            <CardTitle className="text-green-700 text-2xl">Zahlung erfolgreich!</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="text-center">
              <p className="text-lg">Vielen Dank für Ihre Bestellung!</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{order?.order_number}</p>
              <p className="text-sm text-gray-500 mt-1">
                Eine Bestätigung wurde an {order?.customer_email} gesendet.
              </p>
            </div>
            
            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold mb-3">Bestellübersicht</h3>
              <div className="space-y-2">
                {order?.items?.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.product_name}</span>
                    <span>{formatPrice(item.total_price)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-3 pt-3 flex justify-between font-bold">
                <span>Gesamtbetrag</span>
                <span className="text-red-600">{formatPrice(order?.total)}</span>
              </div>
            </div>
            
            {/* Payment Status */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="mx-auto text-green-600 mb-2" size={24} />
              <p className="font-bold text-green-700">Bezahlt via PayPal</p>
              {order?.paypal_transaction_id && (
                <p className="text-sm text-green-600 mt-1">
                  Transaktions-ID: {order.paypal_transaction_id}
                </p>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`${API_URL}/api/orders/invoice/${order?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button variant="outline" className="w-full">
                  <Download className="mr-2" size={18} />
                  Rechnung herunterladen
                </Button>
              </a>
              
              <Button 
                onClick={() => navigate('/')}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Zurück zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PayPalSuccess;
